import { PointerEventEmitter } from './pointer-event-emitter';
import { ReplaySubject, Subscription } from 'rxjs';
import type { Visualize3dObject } from '../models/visualize3d-object';
import { ComponentHandlerProvider } from '../v3d-components/handlers/component-handler-provider';
import {
  instanceOfOnObject3dClick,
  instanceOfOnObject3dOnPointerUp,
  instanceOfOnObject3dOnPointerDown,
  instanceOfOnObject3dOnPointerEnter,
  instanceOfOnObject3dOnPointerExit,
  instanceOfOnObject3dSelect,
  instanceOfOnObject3dDeselect,
} from './object-3d-events';
import { PointerEventData } from './pointer-event-data';
import { Visualize3dObjectEventService } from '../providers/visualize3d-object-event.service';
import { getRelatedThreeJsObject } from '../utility/viewer/v3d-threejs-utility';
import { Object3D, Scene } from 'three';
import type { Visualize3dUserData } from '../models/visualize3d-user-data';

/**
 * Maps pointer events from THREE.Object3ds into V3dObjects and dispatches event to component handlers
 */
export class ViewEventHandler {
  private subscriptions: Subscription[] = [];

  public onClick = new ReplaySubject<Visualize3dObject>(1);
  public onSelect = new ReplaySubject<Visualize3dObject>(1);
  public onDeselect = new ReplaySubject<Visualize3dObject>(1);
  public onDragStart = new ReplaySubject<Visualize3dObject>(1);
  public onDragEnd = new ReplaySubject<Visualize3dObject>(1);
  public onUp = new ReplaySubject<Visualize3dObject>(1);
  public onDown = new ReplaySubject<Visualize3dObject>(1);
  public onEnter = new ReplaySubject<Visualize3dObject>(1);
  public onExit = new ReplaySubject<Visualize3dObject>(1);

  private selectedObject: [Object3D, Visualize3dObject];

  constructor(
    pointerEventEmitter: PointerEventEmitter,
    private componentHandlerProvider: ComponentHandlerProvider,
    private scene: Scene,
    visualize3dObjectEventService: Visualize3dObjectEventService,
  ) {
    this.subscriptions.push(pointerEventEmitter.onPointerEnter.subscribe(event => this.onPointerEnter(event)));
    this.subscriptions.push(pointerEventEmitter.onPointerExit.subscribe(event => this.onPointerExit(event)));
    this.subscriptions.push(pointerEventEmitter.onPointerDown.subscribe(event => this.onPointerDown(event)));
    this.subscriptions.push(pointerEventEmitter.onPointerUp.subscribe(event => this.onPointerUp(event)));
    this.subscriptions.push(pointerEventEmitter.onClick.subscribe(event => this.onPointerClick(event)));
    this.subscriptions.push(pointerEventEmitter.onDragStart.subscribe(event => this.onPointerDragStart(event)));
    this.subscriptions.push(pointerEventEmitter.onDragEnd.subscribe(event => this.onPointerDragEnd(event)));

    this.subscriptions.push(
      visualize3dObjectEventService.v3dObjectSelectionChanged.subscribe(event => {
        let object3d: Object3D | undefined;
        let v3dObject: Visualize3dObject | undefined;
        if (event.v3dObject) {
          object3d = getRelatedThreeJsObject(this.scene, event.v3dObject);
          if (object3d) {
            v3dObject = event.v3dObject;
          }
        }
        this.setSelectedObject(object3d, v3dObject, { emitEvent: false });
      }),
    );
  }

  dispose(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }

    this.scene = undefined;
  }

  private onPointerDragStart(event: PointerEventData): void {
    const [v3dObject] = this.findVisualize3dObject(event.selectedObject);
    this.onDragStart.next(v3dObject);
  }

  private onPointerDragEnd(event: PointerEventData): void {
    const [v3dObject] = this.findVisualize3dObject(event.selectedObject);
    this.onDragEnd.next(v3dObject);
  }

  private onPointerClick(event: PointerEventData): void {
    const [v3dObject, object3d] = this.findVisualize3dObject(event.selectedObject);
    this.setSelectedObject(object3d, v3dObject);
    if (v3dObject) {
      for (const component of v3dObject.components) {
        const handler = this.componentHandlerProvider.getHandler(component.type);
        if (handler && instanceOfOnObject3dClick(handler)) {
          handler.onClick(object3d, v3dObject, component);
        }
      }
    }
    this.onClick.next(v3dObject);
  }

  private onPointerUp(event: PointerEventData): void {
    const [v3dObject, object3d] = this.findVisualize3dObject(event.selectedObject);
    if (v3dObject) {
      for (const component of v3dObject.components) {
        const handler = this.componentHandlerProvider.getHandler(component.type);
        if (handler && instanceOfOnObject3dOnPointerUp(handler)) {
          handler.onPointerUp(object3d, v3dObject, component);
        }
      }
    }
    this.onUp.next(v3dObject);
  }

  private onPointerDown(event: PointerEventData): void {
    const [v3dObject, object3d] = this.findVisualize3dObject(event.selectedObject);
    if (v3dObject) {
      for (const component of v3dObject.components) {
        const handler = this.componentHandlerProvider.getHandler(component.type);
        if (handler && instanceOfOnObject3dOnPointerDown(handler)) {
          handler.onPointerDown(object3d, v3dObject, component);
        }
      }
    }
    this.onDown.next(v3dObject);
  }

  private onPointerEnter(event: PointerEventData): void {
    if (!event) {
      return;
    }
    for (const object of event.pointerEnterObjects) {
      const [v3dObject, object3d] = this.findVisualize3dObject(object);
      if (!v3dObject) {
        continue;
      }

      /**
       * Find out if pointer exited any Object3D and then entered another Object3D both of which
       * belong to the same Object3D tree representing the Visualize3D object.
       *
       * In this case, we have already previously emitted the onPointerEnter event for the given Visualize3D object.
       */
      const userData = object3d.userData as Visualize3dUserData;
      if (event.pointerLeaveObjects.some(b => userData.objects.some(a => b === a))) {
        continue;
      }

      for (const component of v3dObject.components) {
        const handler = this.componentHandlerProvider.getHandler(component.type);
        if (handler && instanceOfOnObject3dOnPointerEnter(handler)) {
          handler.onPointerEnter(object3d, v3dObject, component);
        }
      }
      this.onEnter.next(v3dObject);
    }
  }

  private onPointerExit(event: PointerEventData): void {
    if (!event) {
      return;
    }
    for (const object of event.pointerLeaveObjects) {
      const [v3dObject, object3d] = this.findVisualize3dObject(object);
      if (!v3dObject) {
        continue;
      }

      /**
       * Find out if pointer is still hovering over some of the Object3Ds representing the Visualize3D object,
       * if so we haven't really exited the Visualize3D object
       */
      const userData = object3d.userData as Visualize3dUserData;
      if (event.hoveredObjects.some(b => userData.objects.some(a => b === a))) {
        continue;
      }

      for (const component of v3dObject.components) {
        const handler = this.componentHandlerProvider.getHandler(component.type);
        if (handler && instanceOfOnObject3dOnPointerExit(handler)) {
          handler.onPointerExit(object3d, v3dObject, component);
        }
      }
      this.onExit.next(v3dObject);
    }
  }

  public setSelectedObject(
    object3d: Object3D | undefined,
    v3dObject: Visualize3dObject | undefined,
    options?: {
      emitEvent?: boolean;
    },
  ): void {
    if (object3d && v3dObject) {
      this.select(object3d, v3dObject, options);
    } else {
      this.deselect(options);
    }
  }

  private select(
    object3d: Object3D | null,
    v3dObject: Visualize3dObject | null,
    options?: {
      emitEvent?: boolean;
    },
  ): void {
    if (this.isSelected(object3d, v3dObject)) {
      return;
    }
    if (this.selectedObject) {
      this.deselect(options);
    }
    this.selectedObject = [object3d, v3dObject];
    if (!options || options.emitEvent) {
      this.onSelect.next(v3dObject);
    }
    for (const component of v3dObject.components) {
      const handler = this.componentHandlerProvider.getHandler(component.type);
      if (handler && instanceOfOnObject3dSelect(handler)) {
        handler.onSelect(object3d, v3dObject, component);
      }
    }
  }

  private deselect(options?: { emitEvent?: boolean }): void {
    if (!this.selectedObject) {
      return;
    }
    const selected = this.selectedObject;
    this.selectedObject = null;
    if (!options || options.emitEvent) {
      this.onDeselect.next(selected[1]);
    }
    for (const component of selected[1].components) {
      const handler = this.componentHandlerProvider.getHandler(component.type);
      if (handler && instanceOfOnObject3dDeselect(handler)) {
        handler.onDeselect(selected[0], selected[1], component);
      }
    }
  }

  private isSelected(oject3d: Object3D, v3dObject: Visualize3dObject): boolean {
    return this.selectedObject && this.selectedObject[0] === oject3d && this.selectedObject[1] === v3dObject;
  }

  /**
   * Find Visualize3DObject and the related root Object3D of the pointer event.
   * Remarks:
   *  Due to the fact that there exists a tree of Object3Ds for any given Visualize3DObject - the target Object3D of the pointer event
   *  may, or may not be the root of the tree. This function finds the root Object3D of the tree which represents the Visualize3dObject.
   * @param object3d Target Object3D of the pointer event
   */
  private findVisualize3dObject(
    object3d: Object3D | undefined,
  ): [v3dObject: Visualize3dObject | undefined, object3d: Object3D | undefined] {
    if (!object3d) {
      return [undefined, undefined];
    }
    let current = object3d;
    while (!(current.userData as Visualize3dUserData)?.v3dObject && current.parent) {
      current = current.parent;
    }
    const v3dObject = (current.userData as Visualize3dUserData)?.v3dObject;
    if (!v3dObject) {
      return [undefined, undefined];
    }
    return [v3dObject, current];
  }
}
